import { useEffect, useState, type ReactNode } from 'react';
import { CheckCircle2, Clock, RefreshCw, Users, AlertTriangle, BellRing } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import timesheetApprovalService, {
  type ApprovalProgressPerson,
  type ApprovalProgressState,
  type PayrollApprovalStatus,
} from '../../services/timesheetApproval';
import { formatDateInTz, formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import { cn } from '../../utils/cn';

const STATE_META: Record<ApprovalProgressState, { label: string; chip: string; badge: string }> = {
  sent_back: { label: 'Sent back', chip: 'border-red-200 bg-red-50 text-red-800', badge: 'bg-red-100 text-red-800' },
  not_submitted: { label: 'Not submitted', chip: 'border-neutral-300 bg-neutral-50 text-neutral-800', badge: 'bg-neutral-200 text-neutral-800' },
  awaiting_supervisor: { label: 'Waiting for supervisor', chip: 'border-amber-200 bg-amber-50 text-amber-800', badge: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', chip: 'border-emerald-200 bg-emerald-50 text-emerald-800', badge: 'bg-emerald-100 text-emerald-800' },
  no_time: { label: 'No time recorded', chip: 'border-neutral-200 bg-white text-neutral-600', badge: 'bg-neutral-100 text-neutral-600' },
};

const ORDER: ApprovalProgressState[] = ['sent_back', 'not_submitted', 'awaiting_supervisor', 'approved', 'no_time'];

type Filter = ApprovalProgressState | 'outstanding';

const nameOf = (p: ApprovalProgressPerson) =>
  [p.first_name, p.last_name].filter(Boolean).join(' ') || p.username;

const h = (n: number) => `${n.toFixed(2).replace(/\.?0+$/, '')} h`;

interface Props {
  startDate: string;
  endDate: string;
  /** '' = everyone (custom range). */
  payFrequency: string;
  /** Bumped by the page on every load, so the panel reloads with it. */
  version: number;
}

/**
 * Where everyone's time stands for the period before payroll runs: approved,
 * waiting on a supervisor, not submitted, or sent back. The approved list
 * below shows only what is finished; this says what is not.
 */
export default function PayrollApprovalProgress({ startDate, endDate, payFrequency, version }: Props) {
  const orgTz = useOrgTimezone();
  const [data, setData] = useState<PayrollApprovalStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('outstanding');
  const [refresh, setRefresh] = useState(0);
  // Per supervisor key ('none' = no supervisor set): a nudge in flight, or its outcome.
  const [reminding, setReminding] = useState<Record<string, { busy?: boolean; message?: string; error?: boolean }>>({});

  useEffect(() => {
    if (!startDate || !endDate) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    timesheetApprovalService
      .getPayrollApprovalStatus({
        start_date: startDate,
        end_date: endDate,
        ...(payFrequency ? { pay_frequency: payFrequency } : {}),
      })
      .then(res => {
        if (cancelled) return;
        if (res.success) setData(res.data);
        else setError('Could not load where approvals stand.');
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load where approvals stand.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, payFrequency, version, refresh]);

  const remind = async (supervisorId: number | null) => {
    const key = supervisorId == null ? 'none' : String(supervisorId);
    setReminding(r => ({ ...r, [key]: { busy: true } }));
    try {
      const res = await timesheetApprovalService.remindSupervisor({
        start_date: startDate,
        end_date: endDate,
        ...(payFrequency ? { pay_frequency: payFrequency } : {}),
        supervisor_id: supervisorId,
      });
      if (!res.success) throw new Error('The reminder could not be sent.');
      const sent = res.data;
      const who = sent.reminded.length === 1 ? sent.reminded[0] : `${sent.reminded.length} people`;
      setReminding(r => ({ ...r, [key]: { message: `Reminder sent to ${who}.` } }));
      setData(d => d && {
        ...d,
        waiting_on: d.waiting_on.map(w =>
          (w.supervisor_id ?? null) === supervisorId ? { ...w, last_reminded_at: sent.reminded_at } : w),
      });
    } catch (err: unknown) {
      setReminding(r => ({
        ...r,
        [key]: { error: true, message: err instanceof Error ? err.message : 'The reminder could not be sent.' },
      }));
    }
  };

  if (!data && loading) {
    return (
      <Card className="mb-4">
        <CardContent className="p-4 text-sm text-neutral-600">Checking where approvals stand…</CardContent>
      </Card>
    );
  }
  if (!data) {
    return error ? (
      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
    ) : null;
  }

  const { counts } = data;
  const withTime = data.people.length - counts.no_time;
  const outstanding = counts.sent_back + counts.not_submitted + counts.awaiting_supervisor;
  const clockedIn = data.people.filter(p => p.clocked_in).length;

  let headline: { icon: ReactNode; title: string; detail: string; tone: string };
  if (withTime === 0 && counts.awaiting_supervisor === 0) {
    headline = {
      icon: <Clock className="h-5 w-5 text-neutral-500 shrink-0" />,
      title: 'No time recorded in this period',
      detail: 'Nobody has clocked any hours between these dates.',
      tone: 'border-neutral-200 bg-white',
    };
  } else if (data.ready) {
    headline = {
      icon: <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />,
      title: 'Approvals are finished — ready for payroll',
      detail: `All ${withTime} ${withTime === 1 ? 'person' : 'people'} with time this period have it approved.`,
      tone: 'border-emerald-200 bg-emerald-50',
    };
  } else if (data.supervisors_done) {
    const left = counts.sent_back + counts.not_submitted;
    headline = {
      icon: <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />,
      title: 'Supervisors are done, but not everyone has submitted',
      detail: `${left} ${left === 1 ? 'person has' : 'people have'} time that is not submitted or was sent back to fix. Supervisors have nothing waiting.`,
      tone: 'border-amber-200 bg-amber-50',
    };
  } else {
    headline = {
      icon: <Clock className="h-5 w-5 text-amber-600 shrink-0" />,
      title: `Waiting on supervisors: ${counts.awaiting_supervisor} ${counts.awaiting_supervisor === 1 ? 'person' : 'people'}`,
      detail: `${counts.approved} of ${withTime} approved so far.`,
      tone: 'border-amber-200 bg-amber-50',
    };
  }

  const shown = data.people.filter(p =>
    filter === 'outstanding' ? p.state !== 'approved' && p.state !== 'no_time' : p.state === filter,
  );

  return (
    <Card className="mb-4">
      <CardContent className="p-4 space-y-4">
        <div className={cn('flex items-start gap-3 rounded-lg border p-3', headline.tone)}>
          {headline.icon}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-neutral-900 break-words">{headline.title}</p>
            <p className="text-sm text-neutral-700 break-words">{headline.detail}</p>
            {clockedIn > 0 && (
              <p className="text-sm text-neutral-700 mt-1">
                {clockedIn} {clockedIn === 1 ? 'person is' : 'people are'} still clocked in on a day in this period.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setRefresh(r => r + 1)}
            disabled={loading}
            className="shrink-0 p-1.5 rounded text-neutral-500 hover:text-neutral-800 hover:bg-white/60 disabled:opacity-50"
            aria-label="Check approvals again"
            title="Check again"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {ORDER.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(f => (f === s ? 'outstanding' : s))}
              aria-pressed={filter === s}
              className={cn(
                'rounded-lg border px-3 py-2 text-left transition',
                STATE_META[s].chip,
                filter === s && 'ring-2 ring-primary-500',
              )}
            >
              <span className="block text-xl font-semibold leading-tight">{counts[s]}</span>
              <span className="block text-xs break-words">{STATE_META[s].label}</span>
            </button>
          ))}
        </div>

        {data.waiting_on.length > 0 && (
          <div>
            <p className="text-sm font-medium text-neutral-800 mb-2 flex items-center gap-1.5">
              <Users className="h-4 w-4 text-neutral-500" /> Waiting on
            </p>
            <ul className="space-y-1.5">
              {data.waiting_on.map(w => {
                const key = w.supervisor_id == null ? 'none' : String(w.supervisor_id);
                const r = reminding[key] ?? {};
                const cooldownMs = (data.remind_cooldown_minutes ?? 60) * 60 * 1000;
                const recent = !!w.last_reminded_at && Date.now() - new Date(w.last_reminded_at).getTime() < cooldownMs;
                return (
                  <li
                    key={key}
                    className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded border border-amber-100 bg-amber-50/50 px-3 py-2 text-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-neutral-900 break-words">
                        {w.supervisor_name ?? 'No supervisor set (HR approves)'}
                      </span>
                      <span className="text-neutral-600">
                        {' · '}{w.people} {w.people === 1 ? 'timesheet' : 'timesheets'}
                        {w.hours > 0 && ` · ${h(w.hours)}`}
                        {w.oldest_submitted_at && ` · waiting since ${formatDateInTz(w.oldest_submitted_at, orgTz)}`}
                      </span>
                      {w.last_reminded_at && (
                        <span className="block text-xs text-neutral-500">
                          Reminded {formatDateTimeInTz(w.last_reminded_at, orgTz)}
                        </span>
                      )}
                      {r.message && (
                        <span className={cn('block text-xs', r.error ? 'text-red-700' : 'text-emerald-700')}>{r.message}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => void remind(w.supervisor_id ?? null)}
                      disabled={r.busy || recent}
                      title={recent ? 'Reminded within the last hour' : undefined}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <BellRing className="h-3.5 w-3.5" />
                      {r.busy ? 'Sending…' : recent ? 'Reminded' : w.supervisor_id == null ? 'Remind HR' : 'Remind'}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div>
          <p className="text-sm font-medium text-neutral-800 mb-2">
            {filter === 'outstanding' ? `Not yet approved (${outstanding})` : `${STATE_META[filter].label} (${counts[filter]})`}
          </p>
          {shown.length === 0 ? (
            <p className="text-sm text-neutral-500">Nobody.</p>
          ) : (
            <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
              {shown.map(p => (
                <PersonRow key={p.user_id} person={p} orgTz={orgTz} />
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function PersonRow({ person: p, orgTz }: { person: ApprovalProgressPerson; orgTz: string }) {
  const pending = p.submissions.filter(s => s.status === 'pending');
  const rejected = p.submissions.filter(s => s.status === 'rejected' && s.rejection_reason);
  const parts: string[] = [];
  if (p.hours.approved > 0) parts.push(`${h(p.hours.approved)} approved`);
  if (p.hours.awaiting_supervisor > 0) parts.push(`${h(p.hours.awaiting_supervisor)} waiting`);
  if (p.hours.not_submitted > 0) parts.push(`${h(p.hours.not_submitted)} not submitted`);
  if (p.hours.sent_back > 0) parts.push(`${h(p.hours.sent_back)} sent back`);

  return (
    <li className="px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium text-neutral-900 break-words min-w-0">{nameOf(p)}</span>
        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', STATE_META[p.state].badge)}>
          {STATE_META[p.state].label}
        </span>
        {p.clocked_in && (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Still clocked in</span>
        )}
      </div>
      <p className="text-neutral-600 break-words">
        Supervisor: {p.supervisor_name ?? 'none set'}
        {parts.length > 0 && ` · ${parts.join(' · ')}`}
        {pending.length > 0 && ` · submitted ${formatDateInTz(pending[0].submitted_at, orgTz)}`}
      </p>
      {p.state === 'sent_back' && rejected.length > 0 && (
        <p className="text-red-700 break-words">Reason: {rejected[rejected.length - 1].rejection_reason}</p>
      )}
    </li>
  );
}
